<template>
    <section>
        <form-modal
            :title="title"
            v-model="modelValue"
            :submit-button="'Submit'"
            @submit="submitForm">
            <section class="vstack gap-3">
                <div class="vstack gap-3"
                    v-if="dcraErrors && dcraErrors.length > 0"
                    role="region"
                    aria-labelledby="error-summary-heading"
                    aria-live="polite"
                    aria-atomic="true">
                    <div
                        id="error-alert"
                        class="alert alert-danger fade show"
                        role="alert"
                        v-if="errorOnlyArray?.length > 0">
                        <h3 id="error-summary-heading" class="alert-heading">Error(s):</h3>
                        <ul class="list-group">
                            <li
                                class="ms-3"
                                style="list-style-type: disc"
                                v-for="(error, index) in errorOnlyArray"
                                :key="`error-${index}`">
                                {{ `${error.code} - ${error.message}` }}
                            </li>
                        </ul>
                    </div>
                    <div
                        id="warning-alert"
                        class="alert alert-warning fade show"
                        role="alert"
                        v-if="warningOnlyArray?.length > 0">
                        <h3 id="warning-summary-heading" class="alert-heading">Warning(s):</h3>
                        <ul class="list-group">
                            <li
                                class="ms-3"
                                style="list-style-type: disc"
                                v-for="(warning, index) in warningOnlyArray"
                                :key="`warning-${index}`">
                                {{ `${warning.code} - ${warning.message}` }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div ref="agencyRef">
                    <label for="agency">Agency<span class="text-danger" aria-label="required">*</span></label>
                    <b-agency
                        id="agency"
                        name="agency"
                        v-model="form.agency"
                        :can-clear="false"
                        :error="errors.agency"
                        error-id="agency-error"
                        :select-first="true"
                        :disabled="props.id > 0"
                        aria-required="true"
                        :aria-invalid="!!errors.agency"
                        :aria-describedby="errors.agency ? 'agency-error' : undefined"></b-agency>
                </div>
                <div ref="lastNameRef">
                    <label for="lastName">Last Name<span class="text-danger" aria-label="required">*</span></label>
                    <b-input
                        id="lastName"
                        name="lastName"
                        type="text"
                        :error="errors.lastName"
                        error-id="lastName-error"
                        required="required"
                        v-model="form.lastName"
                        aria-required="true"
                        :aria-invalid="!!errors.lastName"
                        :aria-describedby="errors.lastName ? 'lastName-error' : undefined" />
                </div>
                <div ref="firstNameRef">
                    <label for="firstName">First Name<span class="text-danger" aria-label="required">*</span></label>
                    <b-input
                        id="firstName"
                        name="firstName"
                        type="text"
                        :error="errors.firstName"
                        error-id="firstName-error"
                        required="required"
                        v-model="form.firstName"
                        aria-required="true"
                        :aria-invalid="!!errors.firstName"
                        :aria-describedby="errors.firstName ? 'firstName-error' : undefined" />
                </div>
                <div ref="middleNameRef">
                    <label for="middleName">Middle Name</label>
                    <b-input
                        id="middleName"
                        name="middleName"
                        type="text"
                        :error="errors.middleName"
                        error-id="middleName-error"
                        v-model="form.middleName"
                        :aria-invalid="!!errors.middleName"
                        :aria-describedby="errors.middleName ? 'middleName-error' : undefined" />
                </div>
                <div ref="sexRef">
                    <label for="sex">Sex<span class="text-danger" aria-label="required">*</span></label>
                    <SelectField
                        id="sex"
                        class="w-100"
                        v-model="form.sex"
                        :select-items="dcraStore.getSex"
                        mode="single"
                        :error="errors.sex"
                        error-id="sex-error"
                        aria-required="true"
                        :aria-invalid="!!errors.sex"
                        :aria-describedby="errors.sex ? 'sex-error' : undefined"
                        ></SelectField>
                </div>
                <div ref="raceRef">
                    <label for="race">Race<span class="text-danger" aria-label="required">*</span></label>
                    <SelectField
                        id="race"
                        class="w-100"
                        v-model="form.race"
                        :select-items="dcraStore.getRace"
                        mode="tags"
                        placeholder="Select all that apply"
                        :error="errors.race"
                        error-id="race-error"
                        :include-show-all-option="false"
                        :hide-selected="true"
                        :close-on-select="true"
                        aria-required="true"
                        :aria-invalid="!!errors.race"
                        :aria-describedby="errors.race ? 'race-error' : undefined"></SelectField>
                </div>
                <div ref="ethnicityRef">
                    <label for="ethnicity">Ethnicity<span class="text-danger" aria-label="required">*</span></label>
                    <SelectField
                        id="ethnicity"
                        class="w-100"
                        v-model="form.ethnicity"
                        :select-items="dcraStore.getEthnicity"
                        mode="single"
                        :error="errors.ethnicity"
                        error-id="ethnicity-error"
                        :min="1"
                        aria-required="true"
                        :aria-invalid="!!errors.ethnicity"
                        :aria-describedby="errors.ethnicity ? 'ethnicity-error' : undefined"></SelectField>
                </div>
                <div ref="birthYearRef">
                    <label for="birthYear">Birth Year<span class="text-danger" aria-label="required">*</span></label>
                    <b-input
                        id="birthYear"
                        name="birthYear"
                        type="number"
                        :error="errors.birthYear"
                        error-id="birthYear-error"
                        required="required"
                        v-model="form.birthYear"
                        :min="new Date().getFullYear() - 123"
                        :placeholder="'YYYY or 9999 for unknown'"
                        aria-required="true"
                        :aria-invalid="!!errors.birthYear"
                        :aria-describedby="errors.birthYear ? 'birthYear-error' : undefined" />
                    <!-- The oldest person ever verified was Jeanne Calment from France, who lived to be 122 years and 164 days old. 
                     So we round this number up to 123 to set the min value.  -->
                </div>
                <div ref="dateOfDeathRef">                  
                    <label for="dateOfDeath">Date and Time of Death<span class="text-danger" aria-label="required">*</span></label>
                    <b-date-picker
                        id="dateOfDeath"
                        v-model="form.dateOfDeath"                        
                        :error="errors.dateOfDeath"
                        error-id="dateOfDeath-error"
                        :month-picker="false"
                        :auto-apply="false"
                        :enable-time-picker="true"
                        :enable-minutes="true"
                        :enable-seconds="false"
                        :max-date="new Date()"
                        :min-date="new Date(props.startDate)"
                        :auto-position="true"
                        aria-required="true"
                        :aria-invalid="!!errors.dateOfDeath"
                        :aria-describedby="errors.dateOfDeath ? 'dateOfDeath-error' : undefined"></b-date-picker>
                </div>
                <div ref="eventLocationTypeRef">
                    <label for="eventLocationType">Event Location Type<span class="text-danger" aria-label="required">*</span></label>
                    <SelectField
                        id="eventLocationType"
                        class="w-100"
                        v-model="form.eventLocationType"
                        :select-items="dcraStore.getEventLocationType"
                        mode="single"
                        :error="errors.eventLocationType"
                        error-id="eventLocationType-error"
                        :min="1"
                        aria-required="true"
                        :aria-invalid="!!errors.eventLocationType"
                        :aria-describedby="errors.eventLocationType ? 'eventLocationType-error' : undefined"></SelectField>
                </div>
                <fieldset class="bg-light">
                    <legend class="fs-5">Location of Death</legend>
                    <div class="pb-2" ref="locationNameRef">
                        <label for="locationName">Location Name</label>
                        <b-input
                            id="locationName"
                            name="locationName"
                            type="text"
                            :error="errors.locationName"
                            error-id="locationName-error"
                            v-model="form.locationName"
                            :aria-invalid="!!errors.locationName"
                            :aria-describedby="errors.locationName ? 'locationName-error' : undefined" />
                    </div>
                    <div class="pb-2" ref="streetAddressRef">
                        <label for="streetAddress">Street Address<span class="text-danger" aria-label="required">*</span></label>
                        <b-input
                            id="streetAddress"
                            name="streetAddress"
                            type="text"
                            :error="errors.streetAddress"
                            error-id="streetAddress-error"
                            required="required"
                            v-model="form.streetAddress"
                            aria-required="true"
                            :aria-invalid="!!errors.streetAddress"
                            :aria-describedby="errors.streetAddress ? 'streetAddress-error' : undefined" />
                    </div>
                    <div class="pb-2" ref="cityRef">
                        <label for="city">City<span class="text-danger" aria-label="required">*</span></label>
                        <b-input
                            id="city"
                            name="city"
                            type="text"
                            :error="errors.city"
                            error-id="city-error"
                            required="required"
                            v-model="form.city"
                            aria-required="true"
                            :aria-invalid="!!errors.city"
                            :aria-describedby="errors.city ? 'city-error' : undefined" />
                    </div>
                    <div class="pb-2" ref="stateRef">
                        <label for="state">State<span class="text-danger" aria-label="required">*</span></label>
                        <SelectField
                            id="state"
                            class="w-100"
                            v-model="form.state"
                            :select-items="dcraStore.getState"
                            mode="single"
                            :error="errors.state"
                            error-id="state-error"
                            :min="1"
                            aria-required="true"
                            :aria-invalid="!!errors.state"
                            :aria-describedby="errors.state ? 'state-error' : undefined"></SelectField>
                    </div>
                    <div class="pb-2" ref="zipCodeRef">
                        <label for="zipCode">Zip Code<span class="text-danger" aria-label="required">*</span></label>
                        <b-input
                            id="zipCode"
                            name="zipCode"
                            type="text"
                            :error="errors.zipCode"
                            error-id="zipCode-error"
                            required="required"
                            v-model="form.zipCode"
                            :maxlength="10"
                            aria-required="true"
                            :aria-invalid="!!errors.zipCode"
                            :aria-describedby="errors.zipCode ? 'zipCode-error' : undefined" />
                    </div>
                </fieldset>
                <div ref="mannerOfDeathRef">
                    <label for="mannerOfDeath">Manner of Death<span class="text-danger" aria-label="required">*</span></label>
                    <SelectField
                        id="mannerOfDeath"
                        class="w-100"
                        v-model="form.mannerOfDeath"
                        :select-items="dcraStore.getMannerOfDeath"
                        mode="single"
                        :error="errors.mannerOfDeath"
                        error-id="mannerOfDeath-error"
                        :min="1"
                        aria-required="true"
                        :aria-invalid="!!errors.mannerOfDeath"
                        :aria-describedby="errors.mannerOfDeath ? 'mannerOfDeath-error' : undefined"></SelectField>
                </div>
                <div v-if="form.mannerOfDeath === 'I'" ref="investigatingAgencyRef">
                    <label for="investigatingAgency">Agency Investigating Death and Expected Completion Date</label>
                    <b-textarea
                        id="investigatingAgency"
                        name="investigatingAgency"
                        :error="errors.investigatingAgency"
                        error-id="investigatingAgency-error"
                        v-model="form.investigatingAgency"
                        :max-length="500"
                        :aria-invalid="!!errors.investigatingAgency"
                        :aria-describedby="errors.investigatingAgency ? 'investigatingAgency-error' : undefined" />
                </div>
                <div v-if="form.mannerOfDeath === 'O'" ref="detailsRef">
                    <label for="details">Details</label>
                    <b-textarea
                        id="details"
                        name="details"
                        :error="errors.details"
                        error-id="details-error"
                        v-model="form.details"
                        :aria-invalid="!!errors.details"
                        :aria-describedby="errors.details ? 'details-error' : undefined" />
                </div>
                <div ref="eventDescriptionRef">
                    <label for="eventDescription">Event Description<span class="text-danger" aria-label="required">*</span></label>
                    <b-textarea
                        id="eventDescription"
                        name="eventDescription"
                        required="required"
                        :error="errors.eventDescription"
                        error-id="eventDescription-error"
                        v-model="form.eventDescription"
                        aria-required="true"
                        :aria-invalid="!!errors.eventDescription"
                        :aria-describedby="errors.eventDescription ? 'eventDescription-error' : undefined" />
                </div>
            </section>
        </form-modal>
    </section>
</template>

<script setup lang="ts">
import {ref, computed, defineProps, watch, onMounted, nextTick} from "vue";
import {Death, DeathErrorWarning, DeathForm} from "./schema";
import {useDcraStore} from "@/stores/dcraStore";
import FormModal from "../Misc/FormModal.vue";
import SelectField from "@/components/Misc/SelectField.vue";
import {BInput, BTextarea, BAgency, BDatePicker} from "@/components/Fields";
import {array, date, number, object, string, ValidationError} from "yup";
import {useFormValidation} from "@/validation";

const modelValue = defineModel({type: Boolean});
const props = defineProps<{
    id: number;
    startDate: String;
    currentState: string;
}>();
const emit = defineEmits(["dismiss", "submit", "update:modelValue"]);

var dcraStore = useDcraStore();

const {handleSubmit, errors, resetValidation} = useFormValidation();

const getDefaultForm = (): DeathForm => ({
    agency: "",
    lastName: "",
    firstName: "",
    middleName: "",
    sex: "",
    race: [],
    ethnicity: "",
    birthYear: "",
    dateOfDeath: undefined,
    locationName: "",
    streetAddress: "",
    city: "",
    state: props.currentState,
    zipCode: "",
    eventLocationType: "",
    mannerOfDeath: "",
    investigatingAgency: "",
    details: "",
    eventDescription: "",
});

const form = ref(getDefaultForm());

// Refs for scrolling to errors
const agencyRef = ref();
const lastNameRef = ref();
const firstNameRef = ref();
const middleNameRef = ref();
const sexRef = ref();
const raceRef = ref();
const ethnicityRef = ref();
const birthYearRef = ref();
const dateOfDeathRef = ref();
const eventLocationTypeRef = ref();
const locationNameRef = ref();
const streetAddressRef = ref();
const cityRef = ref();
const stateRef = ref();
const zipCodeRef = ref();
const mannerOfDeathRef = ref();
const investigatingAgencyRef = ref();
const detailsRef = ref();
const eventDescriptionRef = ref();

const fieldRefs = {
    agency: agencyRef,
    lastName: lastNameRef,
    firstName: firstNameRef,
    middleName: middleNameRef,
    sex: sexRef,
    race: raceRef,
    ethnicity: ethnicityRef,
    birthYear: birthYearRef,
    dateOfDeath: dateOfDeathRef,
    eventLocationType: eventLocationTypeRef,
    locationName: locationNameRef,
    streetAddress: streetAddressRef,
    city: cityRef,
    state: stateRef,
    zipCode: zipCodeRef,
    mannerOfDeath: mannerOfDeathRef,
    investigatingAgency: investigatingAgencyRef,
    details: detailsRef,
    eventDescription: eventDescriptionRef,
};

const schema = object<DeathForm>({
    agency: string().required("Agency is a required field"),
    lastName: string()
        .required("Last Name is a required field")
        .matches(/^[0-9a-zA-Z, .\-\']*$/, "Input contains invalid characters"),
    firstName: string()
        .required("First Name is a required field")
        .matches(/^[0-9a-zA-Z, .\-\']*$/, "Input contains invalid characters"),
    middleName: string().matches(/^[0-9a-zA-Z, .\-\']*$/, "Input contains invalid characters"),
    sex: string().required("Sex is a required field"),
    race: array().required("Race is a required field").min(1, "Race is a required field"),
    ethnicity: string().required("Ethnicity is a required field"),
    birthYear: number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .min(new Date().getFullYear() - 123)
        .required("Birth Year is a required field"),
    dateOfDeath: date().required("Date and Time of Death is a required field"),
    locationName: string().matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters"),
    streetAddress: string()
        .required("Street Address is a required field")
        .matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters"),
    city: string()
        .required("City is a required field")
        .matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters"),
    state: string().required("State is required field").max(2),
    zipCode: string()
        .matches(/^\d{5}(-\d{4})?$/, "Zip Code must be in the format 12345 or 12345-6789")
        .required("Zip Code is a required field")
        .max(10),
    eventLocationType: string().required("Event Location Type is a required field"),
    mannerOfDeath: string().required("Manner of Death is a required field"),
    investigatingAgency: string().max(500,"Agency investigating must be less than 500 characters"),
    details: string()
        .matches(/^[0-9a-zA-Z!@#$%, .\-\']*$/, "Input contains invalid characters")
        .max(500, "Details must be less than 500 characters"),
    eventDescription: string().required("Event Description is a required field").max(500,"Event Description must be less than 500 characters"),
});

var submitDisabled = ref(false);
var dcraErrors = ref([] as DeathErrorWarning[]);
const formLoadVersion = ref(0);
const errorLoadVersion = ref(0);
//Computed
const title = computed(() => (props.id > 0 ? "Edit Death" : "Add Death"));

//FUNctions
const submitForm = async () => {
    const isValid = await handleSubmit(schema, form.value);
    if (isValid) {
        if (props.id > 0) {
            await dcraStore.editDeath(props.id, form.value);
        } else {
            await dcraStore.createDeath(form.value);
        }

        window.location.href = "dataupload.aspx?source=8";
    } else {
        // Scroll to first error and focus for screen reader announcement
        await nextTick();
        const fieldOrder = ['agency', 'lastName', 'firstName', 'middleName', 'sex', 'race', 'ethnicity', 'birthYear', 'dateOfDeath', 'eventLocationType', 'locationName', 'streetAddress', 'city', 'state', 'zipCode', 'mannerOfDeath', 'investigatingAgency', 'details', 'eventDescription'];
        for (const field of fieldOrder) {
            if (errors.value[field]) {
                const el = fieldRefs[field]?.value;
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    // Focus first invalid input for screen reader to announce the error
                    const inputEl = el.querySelector('input, select, textarea, [role=\"listbox\"], .vue-select') as HTMLElement;
                    if (inputEl) {
                        inputEl.focus();
                    }
                    break;
                }
            }
        }
    }
};
var errorOnlyArray = computed(()=>{
    let errorArray = [] as DeathErrorWarning[];
    for (const error of dcraErrors.value){
        if(error.kind == "0"){
            errorArray.push(error);
        }
    }
    return errorArray;
})
var warningOnlyArray = computed(()=>{
    let warningArray = [] as DeathErrorWarning[];
    for (const error of dcraErrors.value){
        if(error.kind == "1"){
            warningArray.push(error);
        }
    }
    return warningArray;
})
const resetFormState = () => {
    formLoadVersion.value += 1;
    errorLoadVersion.value += 1;
    resetValidation();
    form.value = getDefaultForm();
    dcraErrors.value = [] as DeathErrorWarning[];
};

const setFormValues = async () => {
    const version = ++formLoadVersion.value;
    resetValidation();
    form.value = getDefaultForm();

    if (props.id > 0) {
        var response = await dcraStore.fetchDeath(props.id);

        if (version !== formLoadVersion.value) {
            return;
        }

        if (response.status == 200) {
            let deathForm = response.data;
            form.value = deathForm;
        }
    }
};

const getItemErrors = async () => {
    const version = ++errorLoadVersion.value;
    dcraErrors.value = [] as DeathErrorWarning[];

    if (props.id > 0) {
        let response = await dcraStore.getDeathErrors(props.id);

        if (version !== errorLoadVersion.value) {
            return;
        }

        if (response != null && response.status == 200) {
            dcraErrors.value = response.data ?? ([] as DeathErrorWarning[]);
        }
    }
};
//LifeCycle Hooks
onMounted(async () => {
    await dcraStore.fetchCodeLabelByKind();
    await setFormValues();
    await getItemErrors();
});

//Watches
watch(
    () => modelValue,
    async (isOpen) => {
        if (!isOpen) {
            resetFormState();
            return;
        }

        resetFormState();
        await setFormValues();
        await getItemErrors();
    },
    {immediate: true}
);

watch(
    () => props.id,
    async () => {
        if (!modelValue.value) {
            return;
        }

        await setFormValues();
        await getItemErrors();
    }
);

watch(
    () => form.value,
    async () => {
        try {
            if (errors.value.length == 0) {
                return;
            }

            errors.value = Array.isArray(errors.value) ? [] : {};
            await schema.validate(form.value, {abortEarly: false});
        } catch (err) {
            const error = err as ValidationError;
            if (error.name !== "ValidationError") throw err;

            // Build an object map of field -> message
            const newErrors: Record<string, string> = {};
            error.inner.forEach((it: ValidationError) => {
                if (it.path) newErrors[it.path] = it.message;
            });
            errors.value = newErrors;
        }
    },
    {deep: true}
);

defineExpose({ resetFormState });
</script>

<style scoped>
.dcra-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
</style>
